import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectPrescriptionViewSettingsData,
  setPrescriptionViewSettingsData,
  setPrescriptionViewSettingsState,
  setPrescriptionViewSettingsError,
} from 'src/clinic/reducers/prescription/prescriptionReducer'
import { defaultConfirmationModalProps } from 'src/consumer/helpers/utilFunctions'
import useSupportPrescriptionTemplateApiActions from './useSupportPrescriptionTemplateApiActions'
import { usePrescriptionTemplateHandlers } from 'src/mentor/components/MentorPrescriptionTemplates/components/hooks/usePrescriptionTemplateHandlers'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

export default function useSupportPrescriptionTemplateFormStates({
  clinicBrands,
  clinicId,
  mentorSlug,
  associatedClinics,
  mentorProfileData,
}) {
  const dispatch = useDispatch()

  const [paperPrescriptionTemplate, setPaperPrescriptionTemplate] = useState()
  const [pdfPrescriptionTemplate, setPdfPrescriptionTemplate] = useState()
  const [pdfOptions, setPdfOptions] = useState()
  const [paperOptions, setPaperOptions] = useState()
  const [prescriptionTemplateType, setPrescriptionTemplateType] = useState('print')
  const [confirmationModalProps, setConfirmationModalProps] = useState(
    defaultConfirmationModalProps,
  )
  const handlers = usePrescriptionTemplateHandlers(
    prescriptionTemplateType === 'print'
      ? setPaperPrescriptionTemplate
      : setPdfPrescriptionTemplate,
  )

  const {
    getAndSetPrescriptionTemplate,
    loading,
    handleSubmit,
    savePrescriptionTemplate,
    addTemplate,
    deleteTemplate,
    activateTemplate,
  } = useSupportPrescriptionTemplateApiActions({
    clinicBrands,
    clinicId,
    mentorSlug,
    associatedClinics,
    mentorProfileData,
    setPaperOptions,
    setPdfOptions,
    paperPrescriptionTemplate,
    setPaperPrescriptionTemplate,
    pdfPrescriptionTemplate,
    setPdfPrescriptionTemplate,
    setConfirmationModalProps,
    selectedTemplateType: prescriptionTemplateType,
  })

  // Fetch prescription view settings using support portal API
  // GET: /api/secure/cc/support/practitioner/settings/final-report-view
  // SAVE: /api/secure/cc/support/practitioner/emr-settings/final-report-view/save
  async function fetchPrescriptionViewSettings() {
    if (!mentorProfileData?.id && !mentorProfileData?.slug) return

    // Check if settings already exist in store
    const existingData = prescriptionViewSettingsData?.data
    const currentState = prescriptionViewSettingsData?.state
    
    // If data exists and state is success, don't fetch again
    if (existingData && currentState === 'success') {
      return existingData
    }
    
    // Only set loading if not already loading to prevent infinite loops
    if (currentState !== 'loading') {
      dispatch(setPrescriptionViewSettingsState('loading'))
    }

    try {
      const params = {
        'emr-based-report-type': 'prescription',
      }
      if (mentorProfileData.slug) {
        params['practitioner-slug'] = mentorProfileData.slug
      } else if (mentorProfileData.id) {
        params['practitioner-id'] = mentorProfileData.id
      }
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/practitioner/settings/final-report-view',
        params,
        undefined,
        undefined,
        true,
      )
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)
        const prescriptionViewSettings = data?.prescriptionViewSettings || data?.data?.prescriptionViewSettings
        if (prescriptionViewSettings) {
          // Update Redux store with settings and set state to success
          dispatch(setPrescriptionViewSettingsData(prescriptionViewSettings))
          dispatch(setPrescriptionViewSettingsState('success'))
        } else {
          dispatch(setPrescriptionViewSettingsState('error'))
        }
      } else {
        dispatch(setPrescriptionViewSettingsState('error'))
      }
    } catch (error) {
      console.error('Error fetching prescription view settings:', error)
      dispatch(setPrescriptionViewSettingsError(error.message || 'Failed to fetch prescription view settings'))
      dispatch(setPrescriptionViewSettingsState('error'))
    }
  }

  const prescriptionViewSettingsData = useSelector(selectPrescriptionViewSettingsData)
  const prescriptionViewSettings = prescriptionViewSettingsData?.data || {}
  const paperPrintSettings = prescriptionViewSettings.paperPrintSettings || {}
  const headerSettings = paperPrintSettings?.headerSettings || {}
  const footerSettings = paperPrintSettings?.footerSettings || {}

  function setupTemplateUsingPrescriptionViewSettings(newTemplateData) {
    if (prescriptionViewSettings) {
      setPaperPrescriptionTemplate((prev) => {
        const prevTemplate = newTemplateData || prev || {}
        const showQr =
          prevTemplate?.patientInfoSectionSettings?.showQrCode ??
          prescriptionViewSettings?.showPrescriptionQR

        return {
          ...prevTemplate,
          header: {
            ...(prevTemplate?.header ?? {}),
            customHeaderHeight:
              prevTemplate?.header?.customHeaderHeight ?? headerSettings?.headerHeight,
            customHeaderType: prevTemplate?.header?.customHeaderType ?? headerSettings?.headerStyle,
            customHeaderBlank:
              prevTemplate?.header?.customHeaderBlank ?? headerSettings?.showBlankHeader,
          },
          footer: {
            ...(prevTemplate?.footer ?? {}),
            customFooterHeight:
              prevTemplate?.footer?.customFooterHeight ?? footerSettings?.footerHeight,
            customFooterType: prevTemplate?.footer?.customFooterType ?? footerSettings?.footerStyle,
            customFooterBlank:
              prevTemplate?.footer?.customFooterBlank ?? footerSettings?.showBlankFooter,
          },
          margins: {
            ...(prevTemplate?.margins ?? {}),
            marginTop: prevTemplate?.margins?.marginTop ?? paperPrintSettings?.marginTop,
            marginRight: prevTemplate?.margins?.marginRight ?? paperPrintSettings?.marginRight,
            marginBottom: prevTemplate?.margins?.marginBottom ?? paperPrintSettings?.marginBottom,
            marginLeft: prevTemplate?.margins?.marginLeft ?? paperPrintSettings?.marginLeft,
          },
          patientInfoSectionSettings: {
            ...(prevTemplate?.patientInfoSectionSettings || {}),
            showQRCode: showQr,
          },
        }
      })
    }
  }

  useEffect(() => {
    // Only setup template if prescriptionViewSettings has actual data
    if (prescriptionViewSettings && Object.keys(prescriptionViewSettings).length > 0) {
      setupTemplateUsingPrescriptionViewSettings()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prescriptionViewSettings?.paperPrintSettings, prescriptionViewSettings?.showPrescriptionQR])

  // Fetch settings early to prevent ViewOnlyPrescriptionV2 from calling the mentor portal API
  useEffect(() => {
    if (mentorProfileData?.id) {
      const existingData = prescriptionViewSettingsData?.data
      const currentState = prescriptionViewSettingsData?.state
      
      // Only fetch if we don't have data or if state is error/idle
      if (!existingData && currentState !== 'loading') {
        fetchPrescriptionViewSettings()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentorProfileData?.id])

  useEffect(() => {
    if (clinicId) {
      getAndSetPrescriptionTemplate()
    }
  }, [clinicId])

  return {
    handlers,
    printPreviewType: prescriptionTemplateType,
    setPrintPreviewType: setPrescriptionTemplateType,
    paperPrescriptionTemplate,
    setPaperPrescriptionTemplate,
    pdfPrescriptionTemplate,
    setPdfPrescriptionTemplate,
    pdfOptions,
    paperOptions,
    paperPrintSettings,
    addTemplate,
    deleteTemplate,
    activateTemplate,
    confirmationModalProps,
    setConfirmationModalProps,
    handleSubmit,
    savePrescriptionTemplate,
    loading,
  }
}

